import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";
import ExerciseActivityScreen from "../../../../../../components/screens/Activity/ExerciseActivityScreen";
import { DateTime } from "luxon";
import { getCourseSectionTopicCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getCourseSectionTopic } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  courses as courseQuerys,
  completions as completionQuerys,
  progress as progressQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getTopicProgress } from "@nualang/nualang-api-and-queries/APIs/Progress";
import {
  courses as courseHelpers,
  completions as completionHelpers,
  progress as progressHelpers,
} from "../../../../../../utils/helpers";
import config from "../../../../../../config";
import { searchStringToObj } from "../../../../../../utils/index";
import { useSubscription } from "../../../../../../hooks";

function Activity(props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [picture, setPicture] = useState("");
  const [completions, setCompletions] = useState([]);
  const [progress, setProgress] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [fromDate, setFromDate] = React.useState(
    DateTime.now().minus({ years: 1 }),
  );
  const [toDate, setToDate] = React.useState(DateTime.now());
  const {
    authData: { username, attributes },
    handleViewExerciseAttempt,
  } = props;
  const location = useLocation();
  const params = useParams();
  const { courseId, sectionId, topicId, exercise } = params;
  const { subscription } = useSubscription();
  const { t } = useTranslation();

  const searchParams = searchStringToObj(location.search);
  const { roleplayId, botId } = searchParams;

  const filterChange = (filter) => {
    setFilterName(filter);
  };

  const fromDateChange = (date) => {
    setFromDate(date);
  };

  const toDateChange = (date) => {
    setToDate(date);
  };

  const courseQuery = courseQuerys.useCourse(
    async () => {
      const course = await getCourse(courseId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};
  const isCreator =
    course.createdBy === username ||
    (course.collaborators &&
      Array.isArray(course.collaborators) &&
      course.collaborators.includes(attributes.email));

  const topicProgressQuery = progressQuerys.useTopicProgress(
    async () => {
      const topicProgress = await getTopicProgress(
        courseId,
        sectionId,
        topicId,
      );
      let formattedProgress = await progressHelpers.formatProgress(
        topicProgress.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      return formattedProgress;
    },
    {
      courseId,
      sectionId,
      topicId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!(courseId && sectionId && topicId) && courseQuery.isSuccess },
  );

  const topicProgress = useMemo(
    () =>
      topicProgressQuery.isSuccess &&
      topicProgressQuery.data &&
      topicProgressQuery.data.length
        ? topicProgressQuery.data
        : [],
    [topicProgressQuery.data, topicProgressQuery.isSuccess],
  );

  const courseSectionTopicCompletionsQuery =
    completionQuerys.useTopicMemberCompletions(
      async (courseId, sectionId, topicId, memberId, returnvalues) => {
        const memberCompletions = await getCourseSectionTopicCompletions(
          courseId,
          sectionId,
          topicId,
          memberId,
          returnvalues,
        );
        const formattedCompletions = await completionHelpers.formatCompletions(
          memberCompletions.Items,
          username,
          attributes.preferred_username,
        );
        return formattedCompletions;
      },
      {
        courseId,
        sectionId,
        topicId,
        memberId: username,
        returnvalues: null,
      },
      { enabled: !!username },
    );

  const topicCompletions = useMemo(
    () =>
      courseSectionTopicCompletionsQuery.isSuccess &&
      courseSectionTopicCompletionsQuery.data &&
      courseSectionTopicCompletionsQuery.data.length
        ? courseSectionTopicCompletionsQuery.data
        : [],
    [
      courseSectionTopicCompletionsQuery.data,
      courseSectionTopicCompletionsQuery.isSuccess,
    ],
  );

  const topicQuery = courseQuerys.useTopic(
    async () => {
      const topic = await getCourseSectionTopic(courseId, sectionId, topicId);
      const formattedTopic = await courseHelpers.formatTopic(
        topic.Item,
        props.authData,
      );
      return formattedTopic;
    },
    {
      courseId,
      sectionId,
      topicId,
      username: props.authData.username,
    },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const courseSectionTopic = useMemo(
    () =>
      topicQuery.isSuccess && topicQuery.data && topicQuery.data.topicId
        ? topicQuery.data
        : {},
    [topicQuery.data, topicQuery.isSuccess],
  );

  useEffect(() => {
    let titleCapitalised = "";
    if (exercise) {
      titleCapitalised = exercise.charAt(0).toUpperCase() + exercise.slice(1);
    }
    if (courseSectionTopic && Object.keys(courseSectionTopic).length > 0) {
      if (exercise === "roleplay") {
        const roleplay = (courseSectionTopic.roleplays || []).find(
          (r) => r.roleplayId === roleplayId,
        );
        if (roleplay) {
          setTitle(`${titleCapitalised} - ${roleplay.roleplayName}`);
          setDescription(roleplay.description);
          setPicture(roleplay.picture);
        }
      } else if (exercise === "bot" || exercise === "bot-listen-and-speak") {
        const bot = (courseSectionTopic.bots || []).find(
          (b) => b.botId === botId,
        );
        if (bot) {
          const modeTitlePrefix =
            exercise === "bot-listen-and-speak" ? t(exercise) : titleCapitalised;
          setTitle(`${modeTitlePrefix} - ${bot.botName}`);
          setDescription(bot.description);
          setPicture(bot.picture);
        }
      } else {
        setTitle(`${titleCapitalised} - ${courseSectionTopic.topicName}`);
      }
    }
  }, [courseSectionTopic, botId, exercise, roleplayId]);

  useEffect(() => {
    let completionsTemp = [];
    if (exercise) {
      completionsTemp = (topicCompletions || []).filter((c) => {
        if (
          c.exercise === exercise ||
          (exercise === "roleplay" && c.exercise.startsWith(exercise))
        ) {
          if (exercise === "roleplay" && roleplayId) {
            return (
              c.exercise.startsWith(exercise) && c.roleplayId === roleplayId
            );
          }
          if (
            (exercise === "bot" || exercise === "bot-listen-and-speak") &&
            botId
          ) {
            return c.exercise === exercise && c.botId === botId;
          }

          return c.exercise === exercise;
        }

        return false;
      });
    } else {
      completionsTemp = topicCompletions;
    }

    setCompletions(completionsTemp);
  }, [topicCompletions, botId, exercise, roleplayId]);

  useEffect(() => {
    let progressTemp = [];
    if (exercise) {
      progressTemp = (topicProgress || []).filter((c) => {
        if (
          c.exercise === exercise ||
          (exercise === "roleplay" && c.exercise.startsWith(exercise))
        ) {
          if (exercise === "roleplay" && roleplayId) {
            return (
              c.exercise.startsWith(exercise) && c.roleplayId === roleplayId
            );
          }
          if (
            (exercise === "bot" || exercise === "bot-listen-and-speak") &&
            botId
          ) {
            return c.exercise === exercise && c.botId === botId;
          }

          return c.exercise === exercise;
        }

        return false;
      });
    } else {
      progressTemp = topicProgress;
    }

    setProgress(progressTemp);
  }, [topicProgress, botId, exercise, roleplayId]);

  return (
    <ExerciseActivityScreen
      t={t}
      filterChange={filterChange}
      fromDate={fromDate}
      toDate={toDate}
      toDateChange={toDateChange}
      fromDateChange={fromDateChange}
      isCreator={isCreator}
      completions={completions}
      completionsTotal={completions.length}
      progress={progress}
      progressTotal={progress.length}
      handleViewEvent={handleViewExerciseAttempt}
      exercise={exercise}
      title={title}
      description={description}
      picture={picture}
      subscription={subscription}
      isCompletionsLoading={courseSectionTopicCompletionsQuery.isLoading}
      isProgressLoading={topicProgressQuery.isLoading}
      mainSiteUrl={config.SITE_URL}
    />
  );
}

Activity.propTypes = {
  authData: PropTypes.object,
  themeContext: PropTypes.object,
  handleViewExerciseAttempt: PropTypes.func,
};

export default Activity;
